import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';

const StaffActivationView = ({
  activatingUser,
  onBackToLogin,
  onActivationSuccess,
  savedDraft = null
}) => {
  // Form State
  const [username, setUsername] = useState(savedDraft?.username || '');
  const [newPassword, setNewPassword] = useState(savedDraft?.newPassword || '');
  const [confirmPassword, setConfirmPassword] = useState(savedDraft?.confirmPassword || '');
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState(savedDraft?.recoveryEmail || '');

  // Submission & Modal State
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const isDevStaging =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'dev.fangdent.com' || process.env.REACT_APP_ENV === 'staging');

  const displayName =
    activatingUser?.name ||
    `${activatingUser?.first_name || ''} ${activatingUser?.last_name || ''}`.trim() ||
    activatingUser?.email ||
    'เจ้าหน้าที่';

  // Sync state if savedDraft updates from parent dynamically
  useEffect(() => {
    if (savedDraft) {
      if (savedDraft.username) setUsername(savedDraft.username);
      if (savedDraft.newPassword) setNewPassword(savedDraft.newPassword);
      if (savedDraft.confirmPassword) setConfirmPassword(savedDraft.confirmPassword);
      if (savedDraft.recoveryEmail) setRecoveryEmail(savedDraft.recoveryEmail);
    }
  }, [savedDraft]);

  // Form Validation
  const cleanUsername = username.trim().toLowerCase();
  const isUsernameValid = /^[a-zA-Z0-9._]{3,30}$/.test(cleanUsername);
  const isPasswordValid = newPassword.length >= 6;
  const isConfirmMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const cleanEmail = recoveryEmail.trim().toLowerCase();
  const isEmailValid = cleanEmail.includes('@') && cleanEmail.includes('.') && cleanEmail.length >= 6;

  const isFormValid =
    isUsernameValid &&
    isPasswordValid &&
    isConfirmMatch &&
    isEmailValid;

  // Handle Registration Submit
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!isFormValid || submitting) return;

    setSubmitting(true);
    setErrorMessage('');

    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Default staff email domain for internal auth
      const authEmail = `${cleanUsername}@fangdent.com`;

      const { error: rpcError } = await supabase.rpc('complete_user_activation', {
        p_user_id: activatingUser.id,
        p_new_email: authEmail,
        p_new_password: newPassword,
        p_line_user_id: null,
        p_line_display_name: null,
        p_line_picture_url: null,
        p_username: cleanUsername,
        p_recovery_email: cleanEmail
      });

      if (rpcError) {
        let msg = rpcError.message || 'เกิดข้อผิดพลาดในการลงทะเบียน';
        if (msg.includes('Username already taken') || msg.includes('user_profiles_username_idx')) {
          msg = `Username "${cleanUsername}" มีผู้ใช้งานแล้ว กรุณาเลือกชื่ออื่น`;
        } else if (msg.includes('Email already registered') || msg.includes('Email already in use')) {
          msg = `อีเมลนี้มีผู้ใช้งานในระบบแล้ว`;
        }
        setErrorMessage(msg);
        setSubmitting(false);
        return;
      }

      // Successful activation -> trigger modal
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Activation submit error:', err);
      setErrorMessage(err.message || 'เกิดข้อผิดพลาดในการเปิดใช้งานบัญชี');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '580px',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          padding: '36px 32px',
          boxSizing: 'border-box'
        }}
      >
        {/* Top Back Button */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={onBackToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: '0.95em',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#0f172a';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            ← กลับสู่หน้าเข้าสู่ระบบ
          </button>

          {isDevStaging && (
            <span
              style={{
                fontSize: '0.75em',
                fontWeight: '700',
                backgroundColor: '#fff7ed',
                color: '#c2410c',
                padding: '4px 10px',
                borderRadius: '9999px',
                border: '1px solid #fed7aa'
              }}
            >
              🧪 Dev Staging
            </span>
          )}
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              backgroundColor: '#ecfdf5',
              color: '#059669',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              marginBottom: '12px'
            }}
          >
            🏥
          </div>
          <h2
            style={{
              margin: '0 0 6px 0',
              fontSize: '1.45em',
              fontWeight: '800',
              color: '#0f172a'
            }}
          >
            เปิดใช้งานบัญชีเจ้าหน้าที่ครั้งแรก
          </h2>
          <div
            style={{
              fontSize: '1.05em',
              color: '#059669',
              fontWeight: '700',
              marginBottom: '4px'
            }}
          >
            ยินดีต้อนรับคุณ {displayName}
          </div>
          <p
            style={{
              margin: '0',
              fontSize: '0.88em',
              color: '#64748b',
              lineHeight: '1.5'
            }}
          >
            กรุณากำหนด Username, Password และระบุอีเมลสำหรับกู้คืนรหัสเพื่อความปลอดภัยในการเข้าสู่ระบบ
          </p>
        </div>

        {/* Global Error Alert */}
        {errorMessage && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1.5px solid #fecaca',
              borderRadius: '12px',
              color: '#b91c1c',
              fontSize: '0.9em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>⚠️</span>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Activation Form */}
        <form onSubmit={handleRegister}>
          {/* 1. Username ใหม่ */}
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="activation-username"
              style={{
                display: 'block',
                fontSize: '0.88em',
                fontWeight: '700',
                color: '#334155',
                marginBottom: '6px'
              }}
            >
              1. Username ใหม่ <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="activation-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="เช่น somchai.j หรือ nurse_ann"
              autoComplete="username"
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                border: isUsernameValid
                  ? '1.5px solid #10b981'
                  : username
                  ? '1.5px solid #f87171'
                  : '1.5px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff'
              }}
            />
            <div
              style={{
                fontSize: '0.78em',
                marginTop: '4px',
                color: isUsernameValid ? '#059669' : username ? '#dc2626' : '#64748b'
              }}
            >
              {isUsernameValid
                ? '✓ Username ถูกต้องตามรูปแบบ'
                : 'ตัวอักษรภาษาอังกฤษ ตัวเลข _ หรือ . (อย่างน้อย 3 ตัวอักษร)'}
            </div>
          </div>

          {/* 2. Password ใหม่ */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label
                htmlFor="activation-password"
                style={{
                  fontSize: '0.88em',
                  fontWeight: '700',
                  color: '#334155'
                }}
              >
                2. Password ใหม่ <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {showPassword ? '🙈 ซ่อนรหัส' : '👁️ แสดงรหัส'}
              </button>
            </div>
            <input
              id="activation-password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="อย่างน้อย 6 ตัวอักษร"
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                border: isPasswordValid
                  ? '1.5px solid #10b981'
                  : newPassword
                  ? '1.5px solid #f87171'
                  : '1.5px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* 3. ยืนยัน Password ใหม่ */}
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="activation-confirm-password"
              style={{
                display: 'block',
                fontSize: '0.88em',
                fontWeight: '700',
                color: '#334155',
                marginBottom: '6px'
              }}
            >
              3. ยืนยัน Password ใหม่ <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="activation-confirm-password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="พิมพ์รหัสผ่านใหม่อีกครั้ง"
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                border: isConfirmMatch
                  ? '1.5px solid #10b981'
                  : confirmPassword
                  ? '1.5px solid #f87171'
                  : '1.5px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            {confirmPassword && (
              <div
                style={{
                  fontSize: '0.78em',
                  marginTop: '4px',
                  color: isConfirmMatch ? '#059669' : '#dc2626'
                }}
              >
                {isConfirmMatch ? '✓ รหัสผ่านตรงกัน' : '✗ รหัสผ่านทั้งสองช่องไม่ตรงกัน'}
              </div>
            )}
          </div>

          {/* 4. อีเมลสำหรับกู้คืนรหัส */}
          <div style={{ marginBottom: '24px' }}>
            <label
              htmlFor="activation-recovery-email"
              style={{
                display: 'block',
                fontSize: '0.88em',
                fontWeight: '700',
                color: '#334155',
                marginBottom: '6px'
              }}
            >
              4. อีเมลสำหรับกู้คืนรหัส <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="activation-recovery-email"
              type="email"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              placeholder="เช่น yourname@gmail.com"
              autoComplete="email"
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                border: isEmailValid
                  ? '1.5px solid #10b981'
                  : recoveryEmail
                  ? '1.5px solid #f87171'
                  : '1.5px solid #cbd5e1',
                fontSize: '0.95em',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: '0.78em', marginTop: '4px', color: '#64748b' }}>
              ใช้อีเมลจริงของท่านเพื่อรับลิงก์รีเซ็ตรหัสผ่านเมื่อลืมรหัส
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isFormValid || submitting}
            style={{
              width: '100%',
              padding: '15px 20px',
              backgroundColor: isFormValid && !submitting ? '#059669' : '#94a3b8',
              color: '#ffffff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '1.05em',
              fontWeight: '700',
              cursor: isFormValid && !submitting ? 'pointer' : 'not-allowed',
              boxShadow: isFormValid && !submitting ? '0 10px 20px -5px rgba(5, 150, 105, 0.4)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            {submitting ? '⏳ กำลังลงทะเบียนและเปิดใช้งาน...' : '✨ ลงทะเบียน'}
          </button>
        </form>
      </div>

      {/* Success Popup Modal */}
      {showSuccessModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            boxSizing: 'border-box'
          }}
        >
          <div
            style={{
              maxWidth: '440px',
              width: '100%',
              backgroundColor: '#ffffff',
              borderRadius: '24px',
              padding: '36px 28px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              textAlign: 'center',
              animation: 'fadeInScale 0.25s ease-out'
            }}
          >
            <div style={{ fontSize: '54px', marginBottom: '14px' }}>🎉</div>
            <h3
              style={{
                fontSize: '1.45em',
                fontWeight: '800',
                color: '#15803d',
                margin: '0 0 12px 0'
              }}
            >
              เปิดใช้งานบัญชีสำเร็จ!
            </h3>
            <p
              style={{
                fontSize: '1.02em',
                color: '#334155',
                lineHeight: '1.6',
                margin: '0 0 24px 0'
              }}
            >
              ยินดีต้อนรับคุณ <strong style={{ color: '#059669' }}>{cleanUsername}</strong> โปรดเข้าสู่ระบบด้วย Username และ Password ที่ท่านได้ตั้งไว้
            </p>
            <button
              type="button"
              onClick={() => onActivationSuccess(cleanUsername)}
              style={{
                width: '100%',
                padding: '14px 20px',
                backgroundColor: '#059669',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '1em',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)'
              }}
            >
              ไปยังหน้าเข้าสู่ระบบ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffActivationView;
